import { useMemo } from 'react'
import { ProjectGridField } from '@/types/fields'
import { getFieldsFromProject } from '@/utils/field'
import {
  DUE_DATE_INFO_TYPE_ID,
  INVOICE_DATE_INFO_TYPE_ID,
  INVOICE_VENDOR_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  LINE_ITEM_JOB_INFO_TYPE_ID,
  LINE_ITEM_PHASE_INFO_TYPE_ID,
  LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID,
  LINE_ITEM_RATE_INFO_TYPE_ID,
  PURCHASE_ORDER_NUMBER_INFO_TYPE_ID,
} from '@/utils/info-types'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useDocumentRowsContext } from '@/components/validation/providers/DocumentRowsProvider'
import useGetValueData from '../validation/useGetValueData'

export type LineItem = {
  rowId: string
  amount: string | null
  description: string | null
  jobNumber: string | null
  jobDisplayName?: string
  phaseNumber: string | null
  phaseDisplayName?: string
  quantity: string | null
  rate: string | null
}

export type DocumentPOMatchingData = {
  vendorName: string | null
  vendorId: string | null
  vendorField: ProjectGridField | null
  purchaseOrderNumber: string | null
  invoiceDate: string | null
  dueDate: string | null
  amountTotal: string | null
  lineItems: LineItem[]
}

export default function useDocumentForPOMatchingData(): DocumentPOMatchingData {
  const { project } = useProjectContext()
  const { baseRow, rowsByGrid } = useDocumentRowsContext()
  const getValueData = useGetValueData()

  return useMemo(() => {
    const baseRowId = baseRow?.id
    if (!baseRowId) {
      return {
        vendorName: null,
        vendorId: null,
        vendorField: null,
        purchaseOrderNumber: null,
        invoiceDate: null,
        dueDate: null,
        amountTotal: null,
        lineItems: [],
      }
    }
    const allFields = getFieldsFromProject(project)
    const fieldsByInfoTypeId = allFields.reduce<
      Record<string, ProjectGridField>
    >((acc, field) => {
      if (!field.info_type_id) return acc
      acc[field.info_type_id] = field
      return acc
    }, {})

    // Find the base-level fields
    const vendorField = fieldsByInfoTypeId[INVOICE_VENDOR_INFO_TYPE_ID]
    const poNumberField = fieldsByInfoTypeId[PURCHASE_ORDER_NUMBER_INFO_TYPE_ID]
    const invoiceDateField = fieldsByInfoTypeId[INVOICE_DATE_INFO_TYPE_ID]
    const dueDateField = fieldsByInfoTypeId[DUE_DATE_INFO_TYPE_ID]

    const vendorData = getValueData({ fieldId: vendorField.id })

    const vendorName = vendorData.value
    const purchaseOrderNumber = getValueData({
      fieldId: poNumberField.id,
    }).value
    const invoiceDate = getValueData({ fieldId: invoiceDateField.id }).value
    const dueDate = getValueData({ fieldId: dueDateField.id }).value

    // Find the line items table field
    const amountField =
      fieldsByInfoTypeId[INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID]
    const descriptionField =
      fieldsByInfoTypeId[INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID]
    const jobField = fieldsByInfoTypeId[LINE_ITEM_JOB_INFO_TYPE_ID]
    const phaseField = fieldsByInfoTypeId[LINE_ITEM_PHASE_INFO_TYPE_ID]
    const quantityField =
      fieldsByInfoTypeId[LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID]
    const rateField = fieldsByInfoTypeId[LINE_ITEM_RATE_INFO_TYPE_ID]

    // Assuming all line item fields belong to the same grid, so we can use any of them to get the rows
    const lineItemRows =
      rowsByGrid[descriptionField?.project_grid_id || ''] || []
    const lineItems: LineItem[] = lineItemRows.map((row) => {
      const jobFieldValue = getValueData({
        fieldId: jobField.id,
        rowId: row.id,
      })
      const phaseFieldValue = getValueData({
        fieldId: phaseField.id,
        rowId: row.id,
      })
      return {
        rowId: row.id,
        amount: getValueData({ fieldId: amountField.id, rowId: row.id }).value,
        description: getValueData({
          fieldId: descriptionField.id,
          rowId: row.id,
        }).value,
        jobNumber: jobFieldValue.jsonValue?.num || null,
        jobDisplayName: jobFieldValue.jsonValue?.pd_display_name,
        phaseNumber: phaseFieldValue.jsonValue?.num || null,
        phaseDisplayName: phaseFieldValue.jsonValue?.pd_display_name,
        quantity: getValueData({ fieldId: quantityField.id, rowId: row.id })
          .value,
        rate: getValueData({ fieldId: rateField.id, rowId: row.id }).value,
      }
    })

    const amountTotal = lineItems
      .reduce(
        (acc: number, li) => acc + (li.amount ? parseFloat(li.amount) : 0),
        0,
      )
      .toString()

    return {
      vendorName,
      vendorId: vendorData.jsonValue?.entity_id || null,
      vendorField,
      purchaseOrderNumber,
      invoiceDate,
      dueDate,
      amountTotal,
      lineItems,
    }
  }, [baseRow?.id, project, getValueData, rowsByGrid])
}
